import { findAccount } from './chartOfAccounts';

/**
 * BWA cost blocks, derived from the Kontierung of the incoming invoices. Modelled on the classic
 * DATEV BWA (Form 01) and mapped onto the customer's own SKR03 account ranges.
 *
 * Honesty is the whole point of this module. FORQ can only see what arrives as a supplier invoice,
 * so two blocks are STRUCTURALLY incomplete and are marked as such rather than shown as zero:
 *   - Personalkosten come from the Lohnbuchhaltung, not from an Eingangsrechnung.
 *   - Abschreibungen are a bookkeeping entry, not an invoice.
 * A figure FORQ cannot know must look unknown — a confident 0 € would be a lie the Geschäftsführung
 * could plan against.
 */
export type BwaBlockKey =
  | 'material'
  | 'personnel'
  | 'rooms'
  | 'taxes'
  | 'insurance'
  | 'vehicles'
  | 'advertising'
  | 'travel'
  | 'goodsOut'
  | 'repairs'
  | 'depreciation'
  | 'other';

export interface BwaBlockDefinition {
  readonly key: BwaBlockKey;
  readonly label: string;
  /** Inclusive account-number ranges (numeric) that belong to this block. */
  readonly ranges: readonly (readonly [number, number])[];
  /**
   * True when supplier invoices cannot cover the block — the figure is then a fragment, never a
   * total, and the UI says so instead of implying completeness.
   */
  readonly incompleteFromInvoices: boolean;
  readonly source: string;
}

export const bwaBlocks: readonly BwaBlockDefinition[] = [
  {
    key: 'material', label: 'Wareneinsatz / Fremdleistungen',
    ranges: [[3000, 3999]], incompleteFromInvoices: false,
    source: 'Wareneingang und Anschaffungsnebenkosten',
  },
  {
    key: 'personnel', label: 'Personalkosten',
    ranges: [[4100, 4199]], incompleteFromInvoices: true,
    source: 'Löhne und Gehälter kommen aus der Lohnbuchhaltung, nicht als Eingangsrechnung',
  },
  {
    key: 'rooms', label: 'Raumkosten',
    ranges: [[4200, 4299]], incompleteFromInvoices: false,
    source: 'Miete, Nebenkosten, Instandhaltung der Räume',
  },
  {
    key: 'taxes', label: 'Betriebliche Steuern',
    ranges: [[4300, 4359]], incompleteFromInvoices: true,
    source: 'Steuervorauszahlungen werden meist direkt gebucht',
  },
  {
    key: 'insurance', label: 'Versicherungen und Beiträge',
    ranges: [[4360, 4399]], incompleteFromInvoices: false,
    source: 'Versicherungen, Beiträge, Gebühren, Abgaben',
  },
  {
    key: 'vehicles', label: 'Fahrzeugkosten',
    ranges: [[4500, 4599]], incompleteFromInvoices: false,
    source: 'KFZ-Steuer, Versicherung, laufende Kosten, Maut, Leasing',
  },
  {
    key: 'advertising', label: 'Werbe- und Repräsentationskosten',
    ranges: [[4600, 4649]], incompleteFromInvoices: false,
    source: 'Werbung und Geschenke',
  },
  {
    key: 'travel', label: 'Reise- und Bewirtungskosten',
    ranges: [[4650, 4699]], incompleteFromInvoices: false,
    source: 'Bewirtung, Reise- und Übernachtungskosten',
  },
  {
    key: 'goodsOut', label: 'Kosten der Warenabgabe',
    ranges: [[4700, 4799]], incompleteFromInvoices: false,
    source: 'Ausgangsfrachten und Verpackung',
  },
  {
    key: 'repairs', label: 'Reparaturen und Instandhaltung',
    ranges: [[4800, 4821]], incompleteFromInvoices: false,
    source: 'Reparaturen, EDV-Kosten, Mietleasing',
  },
  {
    key: 'depreciation', label: 'Abschreibungen',
    ranges: [[4822, 4899]], incompleteFromInvoices: true,
    source: 'Abschreibungen sind eine Buchung, keine Rechnung',
  },
  {
    key: 'other', label: 'Sonstige betriebliche Aufwendungen',
    ranges: [[4900, 4999]], incompleteFromInvoices: false,
    source: 'Porto, Telefon, Büromaterial, Rechts- und Beratungskosten',
  },
];

const blockIndex = new Map<BwaBlockKey, BwaBlockDefinition>(bwaBlocks.map((block) => [block.key, block]));

export function bwaBlock(key: BwaBlockKey): BwaBlockDefinition {
  const block = blockIndex.get(key);
  if (!block) throw new Error(`Unbekannter BWA-Block: ${key}`);
  return block;
}

/**
 * Which BWA block an expense account belongs to, or null when it is outside the operating result
 * (2xxx Zinsen/Steuern, balance-sheet accounts). Unknown accounts return null on purpose — being
 * counted into a random block would be worse than being reported as unassigned.
 */
export function bwaBlockForAccount(accountNumber: string): BwaBlockKey | null {
  const account = findAccount(accountNumber);
  if (!account || account.kind !== 'guv') return null;
  const value = Number(account.number);
  if (!Number.isFinite(value)) return null;
  for (const block of bwaBlocks) {
    for (const [from, to] of block.ranges) {
      if (value >= from && value <= to) return block.key;
    }
  }
  return null;
}
